import React, { useState, useEffect, useCallback } from 'react';
import { getFichajeEstado, ficharEntrada, ficharSalida, getFichajes, getUsuarios, getFichajesSinSalida, getFichajesConfig, updateFichajesConfig } from '../services/api';
import './Fichajes.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function formatDuracion(minutos) {
  if (!minutos && minutos !== 0) return '—';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m}min`;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

const TZ = 'Europe/Madrid';

// El servidor devuelve ISO sin "Z" → forzamos UTC para la conversión correcta
function asUTC(isoStr) {
  if (!isoStr) return null;
  return isoStr.endsWith('Z') || isoStr.includes('+') ? isoStr : isoStr + 'Z';
}

function formatHora(isoStr) {
  if (!isoStr) return '—';
  return new Date(asUTC(isoStr)).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
}

function formatFecha(isoStr) {
  if (!isoStr) return '—';
  return new Date(asUTC(isoStr)).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
}

function ElapsedTimer({ desde }) {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(asUTC(desde)).getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [desde]);

  return <span className="fich-timer">{elapsed}</span>;
}

export default function Fichajes({ user }) {
  const [estado, setEstado] = useState(null);   // { fichado, fichaje }
  const [fichajes, setFichajes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notas, setNotas] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [alertasSinSalida, setAlertasSinSalida] = useState([]);
  const [umbralHoras, setUmbralHoras] = useState(10);
  const [umbralInput, setUmbralInput] = useState('10');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUsuarioId, setPdfUsuarioId] = useState('');
  const [pdfMes, setPdfMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [pdfLoading, setPdfLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  // can_gestionar viene del backend (admin, superadmin o cualquier rol con permiso 'fichaje.gestionar')
  const canVerTodos = estado?.can_gestionar ?? (user?.is_superadmin || user?.rol === 'administrador');
  const LIMIT = 20;

  const cargarEstado = useCallback(async () => {
    try {
      const res = await getFichajeEstado();
      setEstado(res.data);
    } catch {
      setEstado({ fichado: false, fichaje: null });
    }
  }, []);

  const filtroNombreRef = React.useRef(filtroNombre);
  const filtroDesdeRef = React.useRef(filtroDesde);
  const filtroHastaRef = React.useRef(filtroHasta);
  const canVerTodosRef = React.useRef(canVerTodos);

  filtroNombreRef.current = filtroNombre;
  filtroDesdeRef.current = filtroDesde;
  filtroHastaRef.current = filtroHasta;
  canVerTodosRef.current = canVerTodos;

  const cargarFichajes = useCallback(async (p = 1) => {
    try {
      const params = { page: p, limit: LIMIT };
      if (filtroDesdeRef.current) params.fecha_desde = filtroDesdeRef.current;
      if (filtroHastaRef.current) params.fecha_hasta = filtroHastaRef.current;
      if (canVerTodosRef.current && filtroNombreRef.current) params.nombre_usuario = filtroNombreRef.current;
      const res = await getFichajes(params);
      setFichajes(res.data.fichajes);
      setTotal(res.data.total);
    } catch {
      setFichajes([]);
    }
  }, []);

  const cargarAlertas = useCallback(async () => {
    try {
      const [resAlertas, resConfig] = await Promise.all([getFichajesSinSalida(), getFichajesConfig()]);
      setAlertasSinSalida(resAlertas.data || []);
      const h = resConfig.data?.umbral_cierre_horas ?? 10;
      setUmbralHoras(h);
      setUmbralInput(String(h));
    } catch {
      setAlertasSinSalida([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([cargarEstado(), cargarFichajes(1), cargarAlertas()]).finally(() => setLoading(false));
  }, [cargarEstado, cargarFichajes, cargarAlertas]);

  const showMsg = (type, msg) => {
    if (type === 'success') { setSuccess(msg); setError(''); }
    else { setError(msg); setSuccess(''); }
    setTimeout(() => { setSuccess(''); setError(''); }, 4000);
  };

  const getUbicacion = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ coords: null, sinPermiso: true });
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ coords: { lat: pos.coords.latitude, lon: pos.coords.longitude }, sinPermiso: false }),
      (err) => resolve({ coords: null, sinPermiso: err.code === 1 }),
      { timeout: 10000, maximumAge: 0, enableHighAccuracy: true }
    );
  });

  const handleEntrada = async () => {
    setActionLoading(true);
    try {
      const { coords, sinPermiso } = await getUbicacion();
      await ficharEntrada(notas, coords?.lat ?? null, coords?.lon ?? null);
      setNotas('');
      if (sinPermiso) {
        showMsg('success', 'Entrada registrada (sin ubicacion: activa el permiso de localizacion en el navegador)');
      } else {
        showMsg('success', 'Entrada registrada correctamente');
      }
      await Promise.all([cargarEstado(), cargarFichajes(1)]);
      setPage(1);
    } catch (e) {
      showMsg('error', e.response?.data?.error || 'Error al fichar entrada');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSalida = async () => {
    setActionLoading(true);
    try {
      const { coords, sinPermiso } = await getUbicacion();
      await ficharSalida(notas, coords?.lat ?? null, coords?.lon ?? null);
      setNotas('');
      if (sinPermiso) {
        showMsg('success', 'Salida registrada (sin ubicacion: activa el permiso de localizacion en el navegador)');
      } else {
        showMsg('success', 'Salida registrada correctamente');
      }
      await Promise.all([cargarEstado(), cargarFichajes(1)]);
      setPage(1);
    } catch (e) {
      showMsg('error', e.response?.data?.error || 'Error al fichar salida');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFiltrar = (e) => {
    e.preventDefault();
    setPage(1);
    cargarFichajes(1);
  };

  const handleGuardarUmbral = async () => {
    const h = parseInt(umbralInput, 10);
    if (!h || h < 1 || h > 24) { showMsg('error', 'El umbral debe ser entre 1 y 24 horas'); return; }
    try {
      await updateFichajesConfig({ umbral_cierre_horas: h });
      setUmbralHoras(h);
      showMsg('success', `Umbral actualizado: cierre automático tras ${h}h`);
    } catch (e) {
      showMsg('error', e.response?.data?.error || 'Error al guardar');
    }
  };

  const handleAbrirPdf = async () => {
    if (usuarios.length === 0) {
      try {
        const res = await getUsuarios();
        const trabajadores = (res.data || []).filter(u => ['administrador', 'conserje'].includes(u.rol));
        setUsuarios(trabajadores);
        if (trabajadores.length > 0) setPdfUsuarioId(String(trabajadores[0].id));
      } catch {}
    }
    setShowPdfModal(true);
  };

  const handleDescargarPdf = async () => {
    if (!pdfUsuarioId || !pdfMes) return;
    setPdfLoading(true);
    try {
      const params = new URLSearchParams({ usuario_id: pdfUsuarioId, mes: pdfMes });
      const res = await fetch(`${API_URL}/fichajes/informe-pdf?${params}`, { credentials: 'include' });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Error'); }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `jornada_${pdfMes}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      setShowPdfModal(false);
    } catch (e) {
      showMsg('error', e.message || 'Error al generar el PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroDesde) params.append('fecha_desde', filtroDesde);
      if (filtroHasta) params.append('fecha_hasta', filtroHasta);
      if (canVerTodos && filtroNombre) params.append('nombre_usuario', filtroNombre);
      const url = `${API_URL}/fichajes/export?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `fichajes_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      showMsg('error', 'Error al exportar fichajes');
    }
  };

  const handlePage = (p) => {
    setPage(p);
    cargarFichajes(p);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const hoy = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: TZ });

  if (loading) {
    return (
      <div className="fich-page">
        <div className="loading-container"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="fich-page">
      {/* Cabecera */}
      <div className="fich-header">
        <div>
          <h1 className="fich-title">Fichajes</h1>
          <p className="fich-subtitle">{hoy.charAt(0).toUpperCase() + hoy.slice(1)}</p>
        </div>
      </div>

      {/* Mensajes */}
      {error && <div className="fich-alert fich-alert--error">{error}</div>}
      {success && <div className="fich-alert fich-alert--success">{success}</div>}

      {/* Panel de configuración de cierre automático */}
      {user?.rol === 'administrador' && (
        <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:10,padding:'12px 18px',marginBottom:16,display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <span style={{fontSize:13,fontWeight:600,color:'#0369a1'}}>Cierre automatico de fichajes:</span>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <input
              type="number" min="1" max="24"
              value={umbralInput}
              onChange={e => setUmbralInput(e.target.value)}
              style={{width:60,padding:'5px 8px',borderRadius:6,border:'1px solid #7dd3fc',fontSize:14,textAlign:'center'}}
            />
            <span style={{fontSize:13,color:'#374151'}}>horas sin salida</span>
            <button className="btn btn-primary btn-sm" onClick={handleGuardarUmbral} style={{padding:'5px 14px'}}>
              Guardar
            </button>
          </div>
          <span style={{fontSize:12,color:'#64748b',marginLeft:'auto'}}>
            Actualmente: cierre a las <strong>{umbralHoras}h</strong> — se ejecuta cada 30 min automaticamente
          </span>
        </div>
      )}

      {/* Alerta: trabajadores sin fichar salida */}
      {canVerTodos && alertasSinSalida.length > 0 && (
        <div style={{background:'#fff7ed',border:'1.5px solid #f97316',borderRadius:10,padding:'14px 18px',marginBottom:16}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <span style={{fontSize:18}}>⚠️</span>
            <span style={{fontWeight:700,color:'#c2410c',fontSize:14}}>
              {alertasSinSalida.length === 1
                ? '1 trabajador/a no ha fichado la salida'
                : `${alertasSinSalida.length} trabajadores no han fichado la salida`}
            </span>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {alertasSinSalida.map(f => {
              const horas = Math.floor(parseFloat(f.horas_abierto));
              const mins = Math.round((parseFloat(f.horas_abierto) - horas) * 60);
              const fechaEntrada = new Date(asUTC(f.fecha_entrada));
              const hoy = new Date();
              const esAyer = fechaEntrada.toLocaleDateString('es-ES', {timeZone: TZ}) !== hoy.toLocaleDateString('es-ES', {timeZone: TZ});
              return (
                <div key={f.id} style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'4px 10px',background:'#fff',borderRadius:7,padding:'8px 12px',border:'1px solid #fed7aa'}}>
                  <span style={{fontSize:16}}>🔴</span>
                  <span style={{fontWeight:600,fontSize:13,color:'#1f2937',flex:'1 1 100px'}}>{f.usuario_nombre}</span>
                  <span style={{fontSize:12,color:'#9a3412',fontWeight:600}}>
                    Lleva {horas}h {mins}m sin fichar salida
                  </span>
                  <span style={{fontSize:12,color:'#6b7280',width:'100%',paddingLeft:26}}>
                    Entrada: {fechaEntrada.toLocaleDateString('es-ES', {day:'2-digit',month:'2-digit', timeZone: TZ})} {fechaEntrada.toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit', timeZone: TZ})}
                    {esAyer && <span style={{marginLeft:6,background:'#fee2e2',color:'#b91c1c',borderRadius:4,padding:'1px 6px',fontSize:11,fontWeight:700}}>DIA ANTERIOR</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tarjeta de fichaje */}
      <div className={`fich-card ${estado?.fichado ? 'fich-card--activo' : 'fich-card--libre'}`}>
        <div className="fich-card-status">
          <div className={`fich-status-dot ${estado?.fichado ? 'fich-status-dot--on' : ''}`} />
          <span className="fich-status-label">
            {estado?.fichado ? 'En jornada' : 'Sin fichar'}
          </span>
        </div>

        {estado?.fichado && estado.fichaje ? (
          <div className="fich-card-info">
            <div className="fich-card-desde">
              Entrada a las <strong>{formatHora(estado.fichaje.fecha_entrada)}</strong>
            </div>
            <ElapsedTimer desde={estado.fichaje.fecha_entrada} />
          </div>
        ) : (
          <div className="fich-card-info">
            <p className="fich-card-hint">Registra tu entrada para iniciar la jornada</p>
          </div>
        )}

        <div className="fich-notas-row">
          <input
            className="fich-notas-input"
            type="text"
            placeholder="Notas opcionales..."
            value={notas}
            onChange={e => setNotas(e.target.value)}
            maxLength={200}
          />
        </div>

        <button
          className={`fich-btn ${estado?.fichado ? 'fich-btn--salida' : 'fich-btn--entrada'}`}
          onClick={estado?.fichado ? handleSalida : handleEntrada}
          disabled={actionLoading}
        >
          {actionLoading ? (
            <span className="fich-btn-spinner" />
          ) : estado?.fichado ? (
            <>
              <span className="fich-btn-icon">🔴</span>
              Fichar Salida
            </>
          ) : (
            <>
              <span className="fich-btn-icon">🟢</span>
              Fichar Entrada
            </>
          )}
        </button>
      </div>

      {/* Filtros */}
      <div className="fich-section">
        <div className="fich-section-header">
          <h2 className="fich-section-title">Historial de fichajes</h2>
          <button className="btn btn-secondary btn-sm fich-export-btn" type="button" onClick={handleExport}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Descargar Excel
          </button>
          {canVerTodos && (
            <button className="btn btn-primary btn-sm" type="button" onClick={handleAbrirPdf} style={{marginLeft: 8}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              Informe PDF
            </button>
          )}
        </div>
        <form className="fich-filtros" onSubmit={handleFiltrar}>
          {canVerTodos && (
            <input
              className="fich-filtro-input"
              type="text"
              placeholder="Buscar por nombre..."
              value={filtroNombre}
              onChange={e => setFiltroNombre(e.target.value)}
            />
          )}
          <input
            className="fich-filtro-input"
            type="date"
            value={filtroDesde}
            onChange={e => setFiltroDesde(e.target.value)}
            title="Desde"
          />
          <input
            className="fich-filtro-input"
            type="date"
            value={filtroHasta}
            onChange={e => setFiltroHasta(e.target.value)}
            title="Hasta"
          />
          <button className="btn btn-primary btn-sm" type="submit">Filtrar</button>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            onClick={() => { setFiltroDesde(''); setFiltroHasta(''); setFiltroNombre(''); setTimeout(() => cargarFichajes(1), 0); }}
          >
            Limpiar
          </button>
        </form>
      </div>

      {/* Tabla */}
      <div className="fich-table-wrapper">
        {fichajes.length === 0 ? (
          <div className="fich-empty">No hay fichajes registrados</div>
        ) : (
          <table className="fich-table">
            <thead>
              <tr>
                <th>Fecha</th>
                {canVerTodos && <th>Usuario</th>}
                <th>Entrada</th>
                <th>Salida</th>
                <th>Duración</th>
                <th>Ubicación</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {fichajes.map(f => (
                <tr key={f.id} className={!f.fecha_salida ? 'fich-row--activo' : ''}>
                  <td>{formatFecha(f.fecha_entrada)}</td>
                  {canVerTodos && <td className="fich-usuario-cell">{f.usuario_nombre || `ID ${f.usuario_id}`}</td>}
                  <td>{formatHora(f.fecha_entrada)}</td>
                  <td>{f.fecha_salida ? formatHora(f.fecha_salida) : <span className="fich-badge-activo">En curso</span>}</td>
                  <td>{f.duracion_minutos != null ? formatDuracion(f.duracion_minutos) : (f.fecha_salida ? '—' : <span className="fich-badge-activo">—</span>)}</td>
                  <td className="fich-ubi-cell">
                    {f.latitud_entrada && f.longitud_entrada ? (
                      <a
                        href={`https://maps.google.com/?q=${f.latitud_entrada},${f.longitud_entrada}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fich-ubi-link"
                        title={`Entrada: ${parseFloat(f.latitud_entrada).toFixed(5)}, ${parseFloat(f.longitud_entrada).toFixed(5)}`}
                      >📍 Entrada</a>
                    ) : null}
                    {f.latitud_salida && f.longitud_salida ? (
                      <a
                        href={`https://maps.google.com/?q=${f.latitud_salida},${f.longitud_salida}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fich-ubi-link"
                        title={`Salida: ${parseFloat(f.latitud_salida).toFixed(5)}, ${parseFloat(f.longitud_salida).toFixed(5)}`}
                      >📍 Salida</a>
                    ) : null}
                    {!f.latitud_entrada && !f.latitud_salida ? '—' : null}
                  </td>
                  <td className="fich-notas-cell">{f.notas || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="fich-pagination">
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => handlePage(page - 1)}>← Anterior</button>
          <span className="fich-page-info">Página {page} de {totalPages} ({total} registros)</span>
          <button className="btn btn-secondary btn-sm" disabled={page === totalPages} onClick={() => handlePage(page + 1)}>Siguiente →</button>
        </div>
      )}

      {/* Modal informe PDF */}
      {showPdfModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => setShowPdfModal(false)}>
          <div style={{background:'#fff',borderRadius:12,padding:'28px 32px',minWidth:340,maxWidth:420,boxShadow:'0 8px 32px rgba(0,0,0,0.18)'}} onClick={e => e.stopPropagation()}>
            <h3 style={{margin:'0 0 18px',fontSize:17,fontWeight:700}}>Generar informe mensual PDF</h3>
            <div style={{marginBottom:14}}>
              <label style={{display:'block',fontSize:13,fontWeight:600,marginBottom:5,color:'#374151'}}>Trabajador/a</label>
              <select
                value={pdfUsuarioId}
                onChange={e => setPdfUsuarioId(e.target.value)}
                style={{width:'100%',padding:'8px 10px',borderRadius:7,border:'1px solid #d1d5db',fontSize:14}}
              >
                {usuarios.map(u => (
                  <option key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''} ({u.rol})</option>
                ))}
              </select>
            </div>
            <div style={{marginBottom:22}}>
              <label style={{display:'block',fontSize:13,fontWeight:600,marginBottom:5,color:'#374151'}}>Mes</label>
              <input
                type="month"
                value={pdfMes}
                onChange={e => setPdfMes(e.target.value)}
                style={{width:'100%',padding:'8px 10px',borderRadius:7,border:'1px solid #d1d5db',fontSize:14,boxSizing:'border-box'}}
              />
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowPdfModal(false)}>Cancelar</button>
              <button className="btn btn-primary btn-sm" onClick={handleDescargarPdf} disabled={pdfLoading || !pdfUsuarioId}>
                {pdfLoading ? 'Generando...' : 'Descargar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
